import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileDown } from "lucide-react";
import { RevealLine } from "@/components/RevealLine";

const TERMS_SECTIONS = [
  {
    title: "Introduction",
    body: "These Terms and Conditions govern your use of the Offcut mobile application, ChippyMate website and related services. By downloading, accessing or using the services, you agree to be bound by these Terms. If you do not agree to these Terms, you must not use the Services.",
  },
  {
    title: "Your Account",
    body: "Keep your account details accurate and your login credentials secure. You are responsible for activity under your account.",
  },
  {
    title: "Subscriptions",
    body: "Paid features require a subscription. Subscriptions automatically renew unless cancelled before the end of the current billing period.",
  },
  {
    title: "Cancellation",
    body: "Subscriptions purchased through Apple or Google must be cancelled through the relevant platform. Deleting your Offcut account does not automatically cancel your subscription.",
  },
  {
    title: "Refunds",
    body: "Subscription fees are generally non-refundable, except where required by law or the applicable subscription provider's policies.",
  },
  {
    title: "Acceptable Use",
    body: "You must use Offcut lawfully and must not misuse, interfere with, hack, scrape or attempt to circumvent the Services or subscription restrictions.",
  },
  {
    title: "Intellectual Content",
    body: "The Services, including all software, graphics, text, logos, trademarks, designs, databases and other materials, are owned by or licensed to Offcut and are protected by intellectual property laws.",
  },
  {
    title: "Your Content",
    body: "You retain ownership of content you upload to Offcut. You give Offcut permission to process and store that content as reasonably necessary to provide and operate the Services.",
  },
  {
    title: "AI Features",
    body: "AI-generated information may not always be accurate. You are responsible for reviewing and verifying AI outputs before relying on them.",
  },
  {
    title: "Professional Use",
    body: "Offcut does not provide engineering, architectural, surveying, legal, financial, safety or building compliance advice. Users remain responsible for independently verifying all measurements, calculations, plans, estimates, and other outputs before relying upon them.",
  },
  {
    title: "Service Availability",
    body: "We work to keep Offcut available and reliable, but cannot guarantee uninterrupted or error-free operation. Features may change or be discontinued.",
  },
  {
    title: "Third-Party Services",
    body: "Offcut relies on third-party providers, including payment, cloud, AI and other service providers, which may have their own terms and policies.",
  },
  {
    title: "Liability",
    body: "Our liability is limited to the extent permitted by law. Nothing in our Terms excludes rights or remedies that cannot legally be excluded under Australian law.",
  },
  {
    title: "Changes",
    body: "We may update the Terms from time to time and will provide notice of material changes where required by law.",
  },
  {
    title: "Privacy",
    body: "Your use of Offcut is also governed by our Privacy Policy.",
  },
];

const Section = ({ section, index }) => (
  <motion.section
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
    className="border-t border-white/10 pt-8"
  >
    <div className="flex items-baseline gap-5">
      <span className="font-brand text-2xl text-white/35">
        {String(index + 1).padStart(2, "0")}
      </span>
      <h2 className="font-brand text-3xl tracking-wide text-white sm:text-4xl">
        {section.title}
      </h2>
    </div>
    <p className="mt-4 text-sm leading-relaxed text-white/70 sm:text-base">
      {section.body}
    </p>
  </motion.section>
);

export const LegalCrossLinks = ({ current }) => {
  const links = [
    { to: "/terms", label: "Terms & Conditions", testId: "crosslink-terms" },
    {
      to: "/acceptable-use",
      label: "Acceptable Use Policy",
      testId: "crosslink-acceptable-use",
    },
    { to: "/privacy", label: "Privacy Policy", testId: "crosslink-privacy" },
    {
      to: "/account-deletion",
      label: "Account Deletion",
      testId: "crosslink-account-deletion",
    },
  ].filter((l) => l.to !== current);

  return (
    <motion.div
      data-testid="legal-crosslinks"
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
      className="mt-16 border-t border-white/10 pt-8"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40">
        Related Policies
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            data-testid={link.testId}
            className="rounded-full border border-white/20 bg-black/30 px-5 py-2.5 text-sm font-bold text-white backdrop-blur-xl transition-colors duration-300 hover:border-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
          >
            {link.label}
          </Link>
        ))}
      </div>
    </motion.div>
  );
};

export default function Terms() {
  return (
    <motion.main
      data-testid="terms-page"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
      className="relative z-10 mx-auto max-w-3xl px-6 pb-32 pt-32 sm:px-10"
    >
      <Link
        to="/"
        data-testid="page-logo-home-link"
        aria-label="Back to home"
        className="block w-fit rounded-lg transition-transform duration-300 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      >
        <img
          src="/chippymate-logo.png"
          alt="Offcut — back to home"
          className="h-auto w-28"
        />
      </Link>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.35em] text-white/50">
        Offcut - ChippyMate
      </p>
      <h1 className="font-brand mt-3 text-6xl tracking-wide text-white sm:text-7xl">
        <RevealLine>Terms &amp; Conditions</RevealLine>
      </h1>
      <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
        Last updated: 2026
      </p>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
        This summary is provided for convenience only. The full Terms and
        Conditions set out the legally binding terms that govern your use of
        Offcut.
      </p>

      <motion.a
        data-testid="terms-pdf-download"
        href="/terms-and-conditions.pdf"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-black/30 px-6 py-3 text-sm font-bold text-white backdrop-blur-xl transition-colors duration-300 hover:border-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      >
        <FileDown className="h-4 w-4" />
        Full Terms &amp; Conditions (PDF)
      </motion.a>

      <div className="mt-16 space-y-12">
        {TERMS_SECTIONS.map((section, i) => (
          <Section key={section.title} section={section} index={i} />
        ))}
      </div>

      <motion.p
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
        className="mt-16 border-t border-white/10 pt-8 text-sm leading-relaxed text-white/70 sm:text-base"
      >
        If you have any questions about these Terms and Conditions, please reach
        out via the{" "}
        <Link
          to="/contact"
          className="font-semibold text-white underline decoration-white/40 underline-offset-4 transition-colors duration-300 hover:decoration-white"
        >
          Contact Us
        </Link>{" "}
        page.
      </motion.p>

      <LegalCrossLinks current="/terms" />
    </motion.main>
  );
}
